import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Flex } from "theme-ui";
import moment from "moment-timezone";

import { themevals } from "theme/themevals";
import BasicButton from "components/forms/BasicButton";
import { useToast } from "components/toast";
import { Pill } from "containers/Leads/List";

import { PROPOSAL_PILL, copyText } from "./GeneratedProposals";
import {
  addMoContact,
  confirmMo,
  dismissMo,
  findContacts,
  getClientMaintenanceMode,
  getMo,
  notClientMo,
  notThemMo,
  reopenMo,
  selectMoContact,
  setClientMaintenanceMode,
  updateMo,
  MAINTENANCE_MODE_LABELS,
  MO_DISMISS_LABELS,
  type MaintenanceMode,
  type MoContact,
  type MoDismissReason,
  type MoRow,
} from "api/MaintenanceOpportunities";

/**
 * One site, worked end to end (MAINTENANCE_OPPORTUNITY_PHASES.md phase 1):
 * find contacts -> tick one or several -> create leads -> create the
 * proposal. A right-side drawer rather than an inline row because the
 * shared TableData has no expandable rows; the table stays a queue and
 * the work happens here. Also mounted by the project-record card.
 */

export const PLACES_CALL_USD = 0.032;

export const STAGE_LABEL: Record<string, { label: string; bg: string; fg: string }> = {
  candidate: { label: "Owner unknown", bg: "#F1EFE8", fg: "#5f5e5a" },
  identified: { label: "Contacts found", bg: "#E6F1FB", fg: "#185FA5" },
  confirmed: { label: "Confirmed", bg: "#EAF3DE", fg: "#27500A" },
  lead_created: { label: "Lead created", bg: "#E6F1FB", fg: "#185FA5" },
  proposal_sent: { label: "Proposal sent", bg: "#FAEEDA", fg: "#633806" },
  won: { label: "Won", bg: "#27500A", fg: "#FFFFFF" },
  lost: { label: "Lost", bg: "#FBEAEA", fg: "#A60000" },
  client_upsell: { label: "Client upsell", bg: "#EEE9F8", fg: "#4B2E83" },
  dismissed: { label: "Dismissed", bg: "#F1EFE8", fg: "#818181" },
};

export const DuePill: React.FC<{ dueInDays: number | null; dueAt: string | null }> = ({ dueInDays, dueAt }) => {
  if (dueInDays == null) return <Pill bg="#F1EFE8" fg="#5f5e5a">no date</Pill>;
  const when = dueAt ? moment.utc(dueAt).format("MMM D, YYYY") : "";
  if (dueInDays < 0) return <Pill bg="#FBEAEA" fg="#A60000" title={when}>{-dueInDays} day{dueInDays === -1 ? "" : "s"} overdue</Pill>;
  if (dueInDays <= 90) return <Pill bg="#FAEEDA" fg="#633806" title={when}>{dueInDays === 0 ? "due today" : `in ${dueInDays} day${dueInDays === 1 ? "" : "s"}`}</Pill>;
  return <Pill bg="#F1EFE8" fg="#5f5e5a" title={when}>in {dueInDays} days</Pill>;
};

const label: React.CSSProperties = { display: "block", fontFamily: "Poppins", fontSize: 11, color: themevals.colors.dkgrey, margin: "10px 0 3px" };
const input: React.CSSProperties = { width: "100%", boxSizing: "border-box", border: "1px solid #EBEBEB", borderRadius: 6, padding: "7px 10px", fontFamily: "Poppins", fontSize: 13 };
const section: React.CSSProperties = { borderTop: "1px solid #E4E7EB", paddingTop: 14, marginTop: 14 };
const h: React.CSSProperties = { fontFamily: "Rubik", fontWeight: 600, fontSize: 14, margin: 0 };
const sub: React.CSSProperties = { fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey };

const OPEN = ["candidate", "identified", "confirmed"];

const ContactLine: React.FC<{ c: MoContact; disabled: boolean; onToggle: (on: boolean) => void }> = ({ c, disabled, onToggle }) => {
  // Not a <label>: the theme uppercases labels, which made every contact
  // shout. A div with an explicit checkbox keeps normal case.
  const name = c.contact || (c.kind === "email" ? c.email : null) || c.phone || "(no name)";
  const general = c.kind === "email" || /\b(general|office|front desk|reception)\b/i.test(`${c.contact ?? ""} ${c.title ?? ""}`);
  const host = (() => {
    const m = String(c.reason ?? "").match(/https?:\/\/([^/\s]+)/i);
    return m ? m[1].replace(/^www\./, "") : null;
  })();
  const source = /Apollo/i.test(c.reason ?? "")
    ? "Apollo"
    : /Hunter/i.test(c.reason ?? "")
      ? "Hunter"
      : host;
  return (
    <div
      onClick={() => !disabled && onToggle(!c.selected)}
      style={{ display: "grid", gridTemplateColumns: "22px 1fr", gap: 10, alignItems: "start", padding: "10px 0", borderBottom: "1px solid #F1EFE8", cursor: disabled ? "default" : "pointer", textTransform: "none" }}
    >
      <input type="checkbox" checked={!!c.selected} disabled={disabled} onChange={(e) => onToggle(e.target.checked)} onClick={(e) => e.stopPropagation()} style={{ marginTop: 3 }} />
      <div style={{ minWidth: 0 }}>
        <div style={{ fontFamily: "Poppins", fontSize: 13, fontWeight: 600, color: "#000" }}>
          {name}
          {c.title && <span style={{ fontWeight: 400, color: themevals.colors.dkgrey }}> &middot; {c.title}</span>}
          {general && <span style={{ marginLeft: 6 }}><Pill bg="#F1EFE8" fg="#5f5e5a">General</Pill></span>}
          {c.leadId && <span style={{ marginLeft: 6 }}><Pill bg="#E6F1FB" fg="#185FA5">lead</Pill></span>}
        </div>
        <div style={{ fontFamily: "Poppins", fontSize: 13, marginTop: 2, wordBreak: "break-word" }}>
          {c.email ? <span>{c.email}</span> : <span style={{ color: "#A60000" }}>No email yet</span>}
          {c.phone && <span style={{ color: themevals.colors.dkgrey }}> &middot; {c.phone}</span>}
        </div>
        {source && <div style={{ fontFamily: "Poppins", fontSize: 11, color: themevals.colors.dkgrey, marginTop: 2 }}>via {source}</div>}
      </div>
    </div>
  );
};

const OpportunityDrawer: React.FC<{ id: string; onClose: () => void; onChanged: () => void; onReviewSend?: (id: string) => void }> = ({ id, onClose, onChanged, onReviewSend }) => {
  const navigate = useNavigate();
  const toast = useToast();
  const [row, setRow] = useState<MoRow | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [org, setOrg] = useState({ endCustomerName: "", endCustomerPhone: "", endCustomerWebsite: "", endCustomerType: "" });
  const [adding, setAdding] = useState<{ contact: string; email: string; phone: string; title: string } | null>(null);
  const [dismissing, setDismissing] = useState<{ reason: MoDismissReason; note: string } | null>(null);
  const [mode, setMode] = useState<{ explicit: MaintenanceMode | null; effective: MaintenanceMode } | null>(null);

  const apply = (res: any, ok?: string) => {
    if (res?.error) {
      toast.add(res.error, "var(--theme-ui-colors-danger)");
      return false;
    }
    if (res?.id && res?.stage) setRow(res);
    if (ok) toast.add(ok, "var(--theme-ui-colors-success)");
    onChanged();
    return true;
  };

  const load = async () => {
    const res: any = await getMo(id);
    if (res?.error) return toast.add(res.error, "var(--theme-ui-colors-danger)");
    setRow(res);
  };

  useEffect(() => {
    load();
  }, [id]); // eslint-disable-line

  useEffect(() => {
    if (!row) return;
    setOrg({
      endCustomerName: row.endCustomer?.name ?? "",
      endCustomerPhone: row.endCustomer?.phone ?? "",
      endCustomerWebsite: row.endCustomer?.website ?? "",
      endCustomerType: row.endCustomer?.type && row.endCustomer.type !== "auto" ? row.endCustomer.type : "",
    });
    if (row.installer?.id && !mode) {
      getClientMaintenanceMode(row.installer.id).then((m: any) => { if (!m?.error) setMode({ explicit: m.explicit, effective: m.effective }); });
    }
  }, [row?.id, row?.endCustomer?.name]); // eslint-disable-line

  if (!row) {
    return (
      <Shell onClose={onClose}>
        <div style={sub}>Loading…</div>
      </Shell>
    );
  }

  const st = STAGE_LABEL[row.stage] ?? STAGE_LABEL.candidate;
  const isOpen = OPEN.includes(row.stage);
  const people = (row.trail ?? []).filter((c) => (c.kind === "person" || c.kind === "email") && !c.rejectedAt);
  const orgCand = (row.trail ?? []).find((c) => (c.kind === "organization" || (!c.kind && c.name)) && !c.rejectedAt && c.name === row.endCustomer?.name);
  const site = row.site.projectName || row.site.name || "Site";
  const cost = row.identifyCached ? "cached, free" : `~$${PLACES_CALL_USD.toFixed(2)}`;

  const run = async (key: string, fn: () => Promise<any>, ok?: string) => {
    setBusy(key);
    const res = await fn();
    setBusy(null);
    return apply(res, ok);
  };

  const doFind = async (force = false) => {
    setBusy("find");
    const fingerprint = (r: any) => JSON.stringify([r?.trail?.length ?? 0, r?.endCustomer?.identifiedAt ?? null, r?.endCustomer?.note ?? null]);
    const before = fingerprint(row);
    const res: any = await findContacts([row.id], force);
    if (res?.pending) {
      // The search outlived the request (deep research can take a minute or
      // two); the server saves it. Wait here until the saved result shows up,
      // so one click is enough.
      for (let i = 0; i < 24; i++) {
        await new Promise((r) => setTimeout(r, 7500));
        const fresh: any = await getMo(row.id);
        if (fresh && !fresh.error && fingerprint(fresh) !== before) {
          setRow(fresh);
          setBusy(null);
          toast.add("Contact search finished.", "var(--theme-ui-colors-success)");
          onChanged();
          return;
        }
      }
      setBusy(null);
      toast.add("Still searching. The results will appear here when it finishes.", "var(--theme-ui-colors-warning)");
      return;
    }
    setBusy(null);
    if (res?.error) return toast.add(res.error, "var(--theme-ui-colors-danger)");
    const r = res?.results?.[0];
    const people = Number(res?.peopleFound ?? 0);
    toast.add(
      r?.endCustomerName
        ? `Found ${r.endCustomerName}${people ? `, ${people} named contact${people === 1 ? "" : "s"}` : ""}${res.emailsFound ? ` and ${res.emailsFound} address${res.emailsFound === 1 ? "" : "es"}` : ""}.`
        : "No organization found — add the owner by hand.",
      r?.endCustomerName ? "var(--theme-ui-colors-success)" : "var(--theme-ui-colors-warning)",
    );
    await load();
    onChanged();
  };

  const saveOrg = () =>
    run("org", () => updateMo(row.id, {
      endCustomerName: org.endCustomerName.trim() || null,
      endCustomerPhone: org.endCustomerPhone.trim() || null,
      endCustomerWebsite: org.endCustomerWebsite.trim() || null,
      endCustomerType: org.endCustomerType || null,
    }), "Saved.");

  const prop = row.proposal ?? null;
  return (
    <Shell onClose={onClose}>
      {/* What and where */}
      <Flex sx={{ justifyContent: "space-between", alignItems: "flex-start", gap: "10px" }}>
        <div>
          <h3 style={{ ...h, fontSize: 17 }}>{site}</h3>
          <div style={sub}>
            {[row.site.line1, row.site.city, row.site.state].filter(Boolean).join(", ")}
            {row.installer ? ` · via ${row.installer.name}` : ""}
          </div>
        </div>
        <Flex sx={{ gap: "6px", flexWrap: "wrap", justifyContent: "flex-end" }}>
          <Pill bg={st.bg} fg={st.fg}>{st.label}</Pill>
          <DuePill dueInDays={row.dueInDays} dueAt={row.dueAt} />
        </Flex>
      </Flex>
      <div style={{ ...sub, marginTop: 8 }}>
        Installed {row.installedAt ? moment.utc(row.installedAt).format("MMM D, YYYY") : "—"}
        {row.yards != null ? ` · ${row.yards} yd` : ""}
        {row.product ? ` · ${row.product}` : ""}
        {" · "}
        {row.site.projectId && <a href={`/projects/${row.site.projectId}`} style={{ color: "#185FA5" }}>project</a>}
        {row.site.projectId && " · "}
        <a href={`/locations/${row.site.locationId}`} style={{ color: "#185FA5" }}>location</a>
      </div>

      {/* The proposal made for this opportunity (2026-10-19) */}
      {prop && (
        <Box sx={{ marginTop: "14px", padding: "10px 12px", background: "#F7F9FC", border: "1px solid #E4E7EB", borderRadius: "8px", fontFamily: "Poppins", fontSize: 13 }}>
          <Flex sx={{ justifyContent: "space-between", alignItems: "center", gap: "8px" }}>
            <span>Proposal {row.sends?.sent ? <Pill bg="#FFF8E1" fg="#7A5A00">{`Sent to ${row.sends.sent}`}</Pill> : <Pill bg={PROPOSAL_PILL[prop.status].bg} fg={PROPOSAL_PILL[prop.status].fg}>{PROPOSAL_PILL[prop.status].label}</Pill>}
              {row.sends?.sent ? <span style={sub}> · {row.sends.opened} opened</span> : null}
              {prop.sentTo ? <span style={sub}> to {prop.sentTo}</span> : null}
              {prop.viewCount ? <span style={sub}> · {prop.viewCount} view{prop.viewCount === 1 ? "" : "s"}</span> : null}
            </span>
            <BasicButton outline scheme="secondary" type="button" keepText onClick={() => navigate(prop.status === "draft" ? `/proposals/${prop.id}/edit` : `/proposals/${prop.id}`)}>Open</BasicButton>
          </Flex>
          {prop.shareUrl && (
            <div style={{ ...sub, marginTop: 6 }}>
              <button type="button" style={{ background: "none", border: "none", padding: 0, color: "#185FA5", cursor: "pointer", fontFamily: "Poppins", fontSize: 12 }}
                onClick={() => copyText(`${prop.shareUrl}${prop.password ? `\nPassword: ${prop.password}` : ""}`, toast, "Link and password copied")}>
                Copy link{prop.password ? " + password" : ""}
              </button>
              {prop.password ? <span> · password <code>{prop.password}</code></span> : <span> · no password</span>}
            </div>
          )}
        </Box>
      )}
      {(row.endCustomer?.name || row.client) && !["dismissed", "won", "lost"].includes(row.stage) && !(row.sends?.sent) && onReviewSend && (
        <Box sx={{ marginTop: "10px" }}>
          <BasicButton type="button" keepText onClick={() => onReviewSend(row.id)}>Review &amp; send</BasicButton>
          <span style={{ ...sub, marginLeft: 8 }}>One email and one proposal link per person you tick.</span>
        </Box>
      )}

      {/* Outcome states */}
      {row.lead && (
        <Box sx={{ marginTop: "14px", padding: "10px 12px", background: "#E6F1FB", borderRadius: "8px" }}>
          <div style={{ fontFamily: "Poppins", fontSize: 13 }}>
            Lead <b>{row.lead.name}</b>{row.lead.status ? ` · ${row.lead.status.replace(/_/g, " ")}` : ""}
            {row.lead.nextActionNote ? ` · next: ${row.lead.nextActionNote}` : ""}
          </div>
          <Flex sx={{ gap: "8px", marginTop: "8px", flexWrap: "wrap" }}>
            {(row.stage === "lead_created" || row.stage === "proposal_sent") && (
              <BasicButton type="button" keepText onClick={() => navigate(`/proposals/new?opportunityId=${row.id}`)}>
                {row.stage === "proposal_sent" ? "New proposal" : "Create proposal"}
              </BasicButton>
            )}
            <BasicButton outline scheme="secondary" type="button" keepText onClick={() => row.lead && navigate(`/leads/${row.lead.id}`)}>Open lead</BasicButton>
          </Flex>
        </Box>
      )}
      {row.stage === "client_upsell" && (
        <Box sx={{ marginTop: "14px", padding: "10px 12px", background: "#EEE9F8", borderRadius: "8px", fontFamily: "Poppins", fontSize: 13 }}>
          {row.client ? <>This site belongs to our client <b>{row.client.name}</b>. Offer them a plan from their record.</> : "The installer owns this site, so it is a client upsell, not a lead."}
          <Flex sx={{ gap: "8px", marginTop: "8px" }}>
            {row.client && <BasicButton outline scheme="secondary" type="button" keepText onClick={() => row.client && navigate(`/clients/${row.client.id}`)}>Open client</BasicButton>}
            <BasicButton outline scheme="secondary" type="button" keepText disabled={busy === "nc"} onClick={() => run("nc", () => notClientMo(row.id), "Back in the lead queue.")}>Not a client upsell</BasicButton>
          </Flex>
        </Box>
      )}
      {row.stage === "dismissed" && (
        <Box sx={{ marginTop: "14px", padding: "10px 12px", background: "#F1EFE8", borderRadius: "8px", fontFamily: "Poppins", fontSize: 13 }}>
          Dismissed: {row.dismissReason ? MO_DISMISS_LABELS[row.dismissReason] : "—"}{row.dismissNote ? ` — ${row.dismissNote}` : ""}
          <Box sx={{ marginTop: "8px" }}>
            <BasicButton outline scheme="secondary" type="button" keepText disabled={busy === "reopen"} onClick={() => run("reopen", () => reopenMo(row.id), "Reopened.")}>Reopen</BasicButton>
          </Box>
        </Box>
      )}

      {/* Who owns it */}
      <div style={section}>
        <Flex sx={{ justifyContent: "space-between", alignItems: "center" }}>
          <h4 style={h}>Who owns the playground</h4>
          {isOpen && (
            <Flex sx={{ gap: "6px" }}>
              <BasicButton outline scheme="secondary" type="button" keepText disabled={busy === "find"} onClick={() => doFind(false)}>
                {busy === "find" ? "Searching…" : `Find contacts · ${cost}`}
              </BasicButton>
              {(row.endCustomer?.note ?? "").includes("last 30 days") && (
                <BasicButton outline scheme="secondary" type="button" keepText disabled={busy === "find"} onClick={() => doFind(true)}>Search again</BasicButton>
              )}
            </Flex>
          )}
        </Flex>
        {row.endCustomer?.note && <div style={{ ...sub, marginTop: 6 }}>{row.endCustomer.note}</div>}
        {row.stage === "identified" && row.endCustomer?.name && (
          <Flex sx={{ marginTop: "8px", gap: "8px", alignItems: "center", padding: "8px 10px", background: "#FFF8E1", borderRadius: 6, fontFamily: "Poppins", fontSize: 12, color: "#7A5A00" }}>
            <span style={{ flex: 1 }}>
              Suggested{row.endCustomer.source ? ` by ${row.endCustomer.source.replace(/_/g, " ")}` : ""}{row.endCustomer.confidence ? ` (${row.endCustomer.confidence} confidence)` : ""}. Not checked by a person yet.
            </span>
            <BasicButton type="button" keepText disabled={busy === "confirm"} onClick={() => run("confirm", () => confirmMo(row.id), "Confirmed.")}>Confirm</BasicButton>
          </Flex>
        )}
        <label style={label}>Organization</label>
        <input style={input} value={org.endCustomerName} disabled={!isOpen} onChange={(e) => setOrg({ ...org, endCustomerName: e.target.value })} placeholder="e.g. Springhead Lake HOA" />
        <Flex sx={{ gap: "10px" }}>
          <Box sx={{ flex: 1 }}>
            <label style={label}>Phone</label>
            <input style={input} value={org.endCustomerPhone} disabled={!isOpen} onChange={(e) => setOrg({ ...org, endCustomerPhone: e.target.value })} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <label style={label}>Owner type</label>
            <select style={input} value={org.endCustomerType} disabled={!isOpen} onChange={(e) => setOrg({ ...org, endCustomerType: e.target.value })}>
              <option value="">—</option>
              <option value="hoa">HOA</option>
              <option value="school">School</option>
              <option value="church">Church</option>
              <option value="park">Park / municipal</option>
              <option value="apartment">Apartments</option>
              <option value="campground">Campground</option>
            </select>
          </Box>
        </Flex>
        <label style={label}>Website</label>
        <input style={input} value={org.endCustomerWebsite} disabled={!isOpen} onChange={(e) => setOrg({ ...org, endCustomerWebsite: e.target.value })} />
        {isOpen && (
          <Flex sx={{ gap: "8px", marginTop: "10px" }}>
            <BasicButton outline scheme="secondary" type="button" keepText disabled={busy === "org"} onClick={saveOrg}>Save</BasicButton>
            {orgCand && orgCand.source !== "manual" && (
              <BasicButton outline scheme="secondary" type="button" keepText disabled={busy === "nt"} onClick={() => run("nt", () => notThemMo(row.id), "Rejected.")}>Not them</BasicButton>
            )}
          </Flex>
        )}
      </div>

      {/* Contacts to make leads of */}
      <div style={section}>
        <Flex sx={{ justifyContent: "space-between", alignItems: "center" }}>
          <h4 style={h}>Contacts</h4>
          {isOpen && !adding && (
            <BasicButton outline scheme="secondary" type="button" keepText onClick={() => setAdding({ contact: "", email: "", phone: "", title: "" })}>Add contact</BasicButton>
          )}
        </Flex>
        <div style={{ ...sub, marginTop: 4 }}>
          Tick everyone who should get the proposal. The organization becomes one lead; each extra person becomes a contact lead beside it.
        </div>
        {people.length === 0 && !adding && <div style={{ ...sub, marginTop: 10 }}>No contacts yet. Run Find contacts, or add one you know.</div>}
        {people.map((c) => (
          <ContactLine key={c.key} c={c} disabled={!isOpen || !!c.leadId} onToggle={(on) => run(`t-${c.key}`, () => selectMoContact(row.id, c.key, on))} />
        ))}
        {adding && (
          <Box sx={{ marginTop: "10px", padding: "10px", background: "#F7F9FC", borderRadius: "8px" }}>
            <Flex sx={{ gap: "8px" }}>
              <input style={input} placeholder="Name" value={adding.contact} onChange={(e) => setAdding({ ...adding, contact: e.target.value })} />
              <input style={input} placeholder="Title" value={adding.title} onChange={(e) => setAdding({ ...adding, title: e.target.value })} />
            </Flex>
            <Flex sx={{ gap: "8px", marginTop: "8px" }}>
              <input style={input} placeholder="Email" value={adding.email} onChange={(e) => setAdding({ ...adding, email: e.target.value })} />
              <input style={input} placeholder="Phone" value={adding.phone} onChange={(e) => setAdding({ ...adding, phone: e.target.value })} />
            </Flex>
            <Flex sx={{ gap: "8px", marginTop: "8px" }}>
              <BasicButton type="button" keepText disabled={busy === "add"} onClick={async () => { if (await run("add", () => addMoContact(row.id, adding), "Contact added.")) setAdding(null); }}>Add</BasicButton>
              <BasicButton outline scheme="secondary" type="button" keepText onClick={() => setAdding(null)}>Cancel</BasicButton>
            </Flex>
          </Box>
        )}
        {isOpen && (
          <Box sx={{ marginTop: "12px" }}>
            <label style={{ ...label, marginTop: 0 }}>Contact route</label>
            <Flex sx={{ gap: "14px", fontFamily: "Poppins", fontSize: 13 }}>
              <label><input type="radio" checked={row.contactRoute !== "via_installer"} onChange={() => run("route", () => updateMo(row.id, { contactRoute: "direct" }))} /> We contact them</label>
              <label><input type="radio" checked={row.contactRoute === "via_installer"} onChange={() => run("route", () => updateMo(row.id, { contactRoute: "via_installer" }))} /> Through {row.installer?.name ?? "the installer"}</label>
            </Flex>
          </Box>
        )}
      </div>

      {/* Installer setting + dismiss */}
      {row.installer && mode && (
        <div style={section}>
          <label style={{ ...label, marginTop: 0 }}>{`All of ${row.installer.name}’s completed sites`}</label>
          <select
            style={input}
            value={mode.explicit ?? ""}
            onChange={async (e) => {
              const v = (e.target.value || null) as MaintenanceMode | null;
              if (!row.installer) return;
              const res: any = await setClientMaintenanceMode(row.installer.id, v);
              if (res?.error) return toast.add(res.error, "var(--theme-ui-colors-danger)");
              setMode({ explicit: res.explicit, effective: res.effective });
              toast.add("Saved. Takes effect on the next refresh.", "var(--theme-ui-colors-success)");
            }}
          >
            <option value="">Automatic ({MAINTENANCE_MODE_LABELS[mode.effective].toLowerCase()})</option>
            {(Object.keys(MAINTENANCE_MODE_LABELS) as MaintenanceMode[]).map((m) => (
              <option key={m} value={m}>{MAINTENANCE_MODE_LABELS[m]}</option>
            ))}
          </select>
        </div>
      )}
      {isOpen && (
        <div style={section}>
          {!dismissing ? (
            <BasicButton outline scheme="danger" type="button" keepText onClick={() => setDismissing({ reason: "builder_still_owns", note: "" })}>Dismiss</BasicButton>
          ) : (
            <Box>
              <label style={{ ...label, marginTop: 0 }}>Why</label>
              <select style={input} value={dismissing.reason} onChange={(e) => setDismissing({ ...dismissing, reason: e.target.value as MoDismissReason })}>
                {(Object.keys(MO_DISMISS_LABELS) as MoDismissReason[]).map((r) => (
                  <option key={r} value={r}>{MO_DISMISS_LABELS[r]}</option>
                ))}
              </select>
              <input style={{ ...input, marginTop: 8 }} placeholder="Note (optional)" value={dismissing.note} onChange={(e) => setDismissing({ ...dismissing, note: e.target.value })} />
              <Flex sx={{ gap: "8px", marginTop: "8px" }}>
                <BasicButton outline scheme="danger" type="button" keepText disabled={busy === "dismiss"} onClick={async () => {
                  setBusy("dismiss");
                  const res: any = await dismissMo([row.id], dismissing.reason, dismissing.note);
                  setBusy(null);
                  if (res?.error) return toast.add(res.error, "var(--theme-ui-colors-danger)");
                  setDismissing(null);
                  toast.add("Dismissed. It comes back on its own if we mulch the site again.", "var(--theme-ui-colors-success)");
                  await load();
                  onChanged();
                }}>Dismiss</BasicButton>
                <BasicButton outline scheme="secondary" type="button" keepText onClick={() => setDismissing(null)}>Cancel</BasicButton>
              </Flex>
            </Box>
          )}
        </div>
      )}
    </Shell>
  );
};

const Shell: React.FC<{ onClose: () => void; children: React.ReactNode }> = ({ onClose, children }) => (
  <>
    <Box onClick={onClose} sx={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.18)", zIndex: 1000 }} />
    <Box
      role="dialog"
      sx={{
        position: "fixed",
        top: 0,
        right: 0,
        bottom: 0,
        width: ["100%", "520px"],
        background: "#fff",
        zIndex: 1001,
        boxShadow: "-6px 0 24px rgba(0,0,0,0.10)",
        overflowY: "auto",
        padding: "20px 22px 40px",
      }}
    >
      <Flex sx={{ justifyContent: "flex-end", marginBottom: "6px" }}>
        <button type="button" onClick={onClose} aria-label="Close" style={{ border: 0, background: "transparent", fontSize: 20, cursor: "pointer", color: themevals.colors.dkgrey }}>×</button>
      </Flex>
      {children}
    </Box>
  </>
);

export default OpportunityDrawer;
